import { t as uiText } from '../../i18n';

const MEMBER_LIMIT_EXCEEDED = 'MEMBER_LIMIT_EXCEEDED';
const EMAIL_VERIFICATION_REQUIRED = 'EMAIL_VERIFICATION_REQUIRED';
const EMAIL_GATE_UNAVAILABLE = 'EMAIL_GATE_UNAVAILABLE';

type CallableDetails = { code?: string; limit?: unknown };

function callableDetails(error: unknown): CallableDetails {
  if (!error || typeof error !== 'object') return {};
  const raw = (error as { details?: unknown; customData?: unknown }).details
    ?? (error as { customData?: unknown }).customData;
  if (!raw || typeof raw !== 'object') return {};
  return raw as CallableDetails;
}

function functionsCode(error: unknown): string {
  if (!error || typeof error !== 'object') return '';
  const code = (error as { code?: unknown }).code;
  return typeof code === 'string' ? code : '';
}

/** メール確認ゲートなど、招待系 callable に共通のエラー。該当しなければ null。 */
export function sharedInviteCallableMessage(error: unknown): string | null {
  const detailCode = callableDetails(error).code;
  if (detailCode === EMAIL_VERIFICATION_REQUIRED) {
    return uiText('Email verification is required. Open the link in the confirmation email to finish.');
  }
  if (detailCode === EMAIL_GATE_UNAVAILABLE) {
    return uiText('Please try again in a moment.');
  }
  return null;
}

export type MemberLimitInfo = { limit: number | null; current: number | null };

/** 人数上限エラーなら上限と現在人数を返す。それ以外は null。 */
export function memberLimitInfo(error: unknown): MemberLimitInfo | null {
  const details = callableDetails(error) as CallableDetails & { current?: unknown };
  const isLimit = details.code === MEMBER_LIMIT_EXCEEDED || functionsCode(error) === 'functions/resource-exhausted';
  if (!isLimit) return null;
  return {
    limit: typeof details.limit === 'number' ? details.limit : null,
    current: typeof details.current === 'number' ? details.current : null,
  };
}

/** 人数上限の通知（PlanLimitNotice）の本文。 */
export function memberLimitNoticeText(info: MemberLimitInfo): {
  title: string;
  message: string;
  usageLabel?: string;
} {
  const title = uiText('Member limit reached');
  const message =
    info.limit !== null
      ? uiText('Your current plan allows up to {{limit}} members. To add more, upgrade your plan or remove existing members.', {
          limit: info.limit,
        })
      : uiText('You have reached the member limit of your current plan. To add more, upgrade your plan or remove existing members.');
  const usageLabel =
    info.limit !== null && info.current !== null
      ? uiText('Current members: {{current}} / {{limit}}', { current: info.current, limit: info.limit })
      : undefined;
  return { title, message, usageLabel };
}

function memberLimitMessage(error: unknown): string | null {
  const details = callableDetails(error);
  if (!memberLimitInfo(error)) return null;
  if (typeof details.limit === 'number') {
    return uiText(
      'The member limit ({{limit}}) has been reached. Upgrade your plan or remove members you no longer need.',
      { limit: details.limit }
    );
  }
  return uiText('The member limit has been reached. Upgrade your plan or remove members you no longer need.');
}

/** メールアドレス指定でメンバーを直接追加したときの表示文。 */
export function directAddMemberErrorMessage(error: unknown): string {
  return (
    sharedInviteCallableMessage(error) ??
    memberLimitMessage(error) ??
    ({
      'functions/already-exists': uiText('This user is already a member of this workspace.'),
      'functions/unauthenticated': uiText('Login required.'),
      'functions/not-found': uiText('No account exists for the specified email address.'),
      'functions/permission-denied': uiText('You do not have permission to add members.'),
      'functions/invalid-argument': uiText('Check your input.'),
    } as Record<string, string>)[functionsCode(error)] ??
    uiText("Couldn't add.")
  );
}

/** 参加申請を承認したときの表示文。 */
export function approveJoinErrorMessage(error: unknown): string {
  return (
    sharedInviteCallableMessage(error) ??
    memberLimitMessage(error) ??
    ({
      'functions/already-exists': uiText('This user is already a member of this workspace.'),
      'functions/unauthenticated': uiText('Login required.'),
      'functions/not-found': uiText('The join request was not found.'),
      'functions/permission-denied': uiText('You do not have permission to add members.'),
      'functions/invalid-argument': uiText('Check your input.'),
    } as Record<string, string>)[functionsCode(error)] ??
    uiText("Couldn't approve the join request.")
  );
}

/** 招待リンク発行に失敗したときの表示文。 */
export function generateInviteLinkErrorMessage(error: unknown): string {
  return (
    sharedInviteCallableMessage(error) ??
    ({
      'functions/unauthenticated': uiText('Login required'),
      'functions/permission-denied': uiText('Administrator permission is required'),
      'functions/not-found': uiText('The specified workspace was not found.'),
      'functions/invalid-argument': uiText('Check your input.'),
    } as Record<string, string>)[functionsCode(error)] ??
    uiText('Failed to create the link')
  );
}

/** 企業メンバー追加に失敗したときの表示文。 */
export function companyMemberAddErrorMessage(error: unknown): string {
  return (
    sharedInviteCallableMessage(error) ??
    ({
      'functions/unauthenticated': uiText('Login required.'),
      'functions/not-found': uiText('No notreu account was found for the specified email address.'),
      'functions/permission-denied': uiText('You do not have permission to add members.'),
      'functions/invalid-argument': uiText('Check your input.'),
    } as Record<string, string>)[functionsCode(error)] ??
    uiText("Couldn't add. Please try again in a moment.")
  );
}

/** 招待リンクを開いた結果（success: false の error コード）の表示文。 */
export function inviteLinkFailureMessage(errorCode: string | undefined): string {
  switch (errorCode) {
    case 'LINK_NOT_FOUND':
      return uiText('This invite link was not found.');
    case 'LINK_INACTIVE':
      return uiText('This invite link has been disabled.');
    case 'LINK_EXPIRED':
      return uiText('This link has expired');
    case 'LINK_USAGE_EXCEEDED':
      return uiText('This invite link has reached its usage limit.');
    case 'WORKSPACE_NOT_FOUND':
      return uiText('Workspace not found');
    case 'ALREADY_MEMBER':
      return uiText('This user is already a member of this workspace.');
    case 'JOIN_REQUEST_PENDING':
      return uiText('A join request is already pending.');
    case MEMBER_LIMIT_EXCEEDED:
      return uiText("This workspace has reached its member limit, so you can't join. Contact an administrator.");
    default:
      return uiText('Failed to process the invite link');
  }
}
